import type { TFormQuestion, TSubmissionResult } from '@onesource/services';
import { Document, Image, Link, Page, StyleSheet, Text, View, pdf } from '@react-pdf/renderer';
import React from 'react';

const BRAND = '#ff4d6d';
const BRAND_DEEP = '#c83a55';
const BRAND_TINT = '#fff0f3';
const INK = '#1a1a1a';
const BODY = '#333333';
const MUTED = '#6b6b6b';
const SUBTLE = '#9b9b9b';
const HAIRLINE = '#ececec';
const CARD = '#fafafa';
const PILL_OFF_BG = '#ffffff';
const PILL_OFF_BORDER = '#dcdcdc';
const PILL_OFF_INK = '#777777';

const styles = StyleSheet.create({
  page: {
    paddingTop: 0,
    paddingBottom: 56,
    paddingHorizontal: 0,
    fontSize: 11,
    color: BODY,
    fontFamily: 'Helvetica',
    backgroundColor: '#ffffff',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 40,
    paddingTop: 28,
    paddingBottom: 18,
    borderBottom: `2pt solid ${BRAND}`,
  },
  logo: {
    width: 110,
    height: 48,
    objectFit: 'contain',
  },
  headerRight: {
    alignItems: 'flex-end',
  },
  headerEyebrow: {
    fontSize: 8,
    color: SUBTLE,
    letterSpacing: 1.2,
    marginBottom: 3,
    textTransform: 'uppercase',
  },
  headerEyebrowSpace: { color: BRAND, fontFamily: 'Helvetica-Bold' },
  headerTitle: {
    fontSize: 18,
    fontFamily: 'Helvetica-Bold',
    color: INK,
    maxWidth: 360,
    textAlign: 'right',
  },
  accentStrip: { height: 4, backgroundColor: BRAND_TINT },
  metaWrap: { paddingHorizontal: 40, paddingTop: 18, paddingBottom: 22 },
  metaRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 18 },
  metaItem: { minWidth: 140 },
  metaLabel: {
    fontSize: 8,
    color: SUBTLE,
    letterSpacing: 1,
    marginBottom: 3,
    textTransform: 'uppercase',
  },
  metaValue: { fontSize: 11, color: INK, fontFamily: 'Helvetica-Bold' },
  sectionDivider: { marginHorizontal: 40, marginBottom: 16, borderBottom: `1pt solid ${HAIRLINE}` },
  body: { paddingHorizontal: 40 },
  card: {
    backgroundColor: CARD,
    borderLeft: `3pt solid ${BRAND}`,
    borderRadius: 4,
    padding: 14,
    marginBottom: 12,
  },
  cardHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  qBadge: {
    backgroundColor: BRAND,
    color: '#fff',
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
    letterSpacing: 0.6,
  },
  qType: {
    fontSize: 8,
    color: SUBTLE,
    marginLeft: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  qUnanswered: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: MUTED,
    marginLeft: 'auto',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
    backgroundColor: '#f0f0f0',
    letterSpacing: 0.6,
  },
  qText: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    color: INK,
    lineHeight: 1.4,
    marginBottom: 4,
  },
  qBody: {
    fontSize: 10,
    color: MUTED,
    lineHeight: 1.5,
    marginBottom: 8,
  },
  sectionLabel: {
    fontSize: 8,
    color: SUBTLE,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    marginBottom: 6,
    marginTop: 4,
  },
  answer: { fontSize: 11, lineHeight: 1.5, color: BODY },
  answerEmpty: { fontSize: 11, fontStyle: 'italic', color: SUBTLE },
  pillRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 4 },
  pillSelected: {
    backgroundColor: BRAND,
    color: '#ffffff',
    fontFamily: 'Helvetica-Bold',
    fontSize: 10,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  pillTintSelected: {
    backgroundColor: BRAND_TINT,
    color: BRAND_DEEP,
    fontSize: 10,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  pillOff: {
    backgroundColor: PILL_OFF_BG,
    color: PILL_OFF_INK,
    fontSize: 10,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    border: `1pt solid ${PILL_OFF_BORDER}`,
  },
  orderingItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 4,
  },
  orderingNumber: { width: 18, fontSize: 10, color: BRAND, fontFamily: 'Helvetica-Bold' },
  bulletItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 3,
  },
  bulletDot: { width: 12, fontSize: 10, color: SUBTLE },
  fileItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    border: `1pt solid ${HAIRLINE}`,
    borderRadius: 4,
    padding: 6,
    marginBottom: 4,
  },
  fileIconBox: {
    width: 28,
    height: 28,
    borderRadius: 3,
    backgroundColor: BRAND_TINT,
    color: BRAND_DEEP,
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
    paddingTop: 9,
    marginRight: 8,
  },
  fileLink: {
    fontSize: 10,
    color: BRAND_DEEP,
    textDecoration: 'underline',
  },
  textBlock: {
    backgroundColor: '#ffffff',
    borderLeft: `2pt solid ${BRAND_TINT}`,
    paddingLeft: 10,
    paddingVertical: 6,
    marginTop: 2,
  },
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 40,
    right: 40,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 8,
    borderTop: `1pt solid ${HAIRLINE}`,
  },
  footerBrand: { fontSize: 8, color: MUTED, letterSpacing: 0.8 },
  footerPage: { fontSize: 8, color: MUTED },
});

// Render inline children (strong, em, a, br, etc.) inside a Text block
const renderInlineChildren = (el: Element, keyPrefix: string): React.ReactNode[] =>
  [...el.childNodes].map((child, i) => {
    const key = `${keyPrefix}-${i}`;
    if (child.nodeType === Node.TEXT_NODE) {
      return child.textContent ?? null;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) {
      return null;
    }
    const c = child as Element;
    const tag = c.tagName.toLowerCase();
    const inner = renderInlineChildren(c, key);
    switch (tag) {
      case 'strong':
      case 'b': {
        return (
          <Text key={key} style={{ fontFamily: 'Helvetica-Bold' }}>
            {inner}
          </Text>
        );
      }
      case 'em':
      case 'i': {
        return (
          <Text key={key} style={{ fontStyle: 'italic' }}>
            {inner}
          </Text>
        );
      }
      case 'u': {
        return (
          <Text key={key} style={{ textDecoration: 'underline' }}>
            {inner}
          </Text>
        );
      }
      case 'a': {
        const href = c.getAttribute('href') ?? '';
        const text = c.textContent ?? '';
        if (href.startsWith('http://') || href.startsWith('https://')) {
          return (
            <Link key={key} src={href} style={{ color: BRAND_DEEP, textDecoration: 'underline', fontSize: 10 }}>
              {text}
            </Link>
          );
        }
        return (
          <Text key={key} style={{ color: BRAND_DEEP, textDecoration: 'underline' }}>
            {text}
          </Text>
        );
      }
      case 'br': {
        return '\n';
      }
      default: {
        return <Text key={key}>{inner}</Text>;
      }
    }
  });

type HtmlRenderContext = 'body' | 'title';

// Convert an HTML string to react-pdf nodes (block-level parsing)
const renderHtmlToPdf = (html: string | null | undefined, context: HtmlRenderContext = 'body'): React.ReactNode => {
  if (!html?.trim() || typeof window === 'undefined') {
    return null;
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  let idx = 0;
  const blocks: React.ReactNode[] = [];

  const isTitle = context === 'title';
  const bodyStyle = isTitle
    ? ({ fontSize: 11, color: INK, lineHeight: 1.4, marginBottom: 3 } as const)
    : ({ fontSize: 10, color: MUTED, lineHeight: 1.5, marginBottom: 3 } as const);
  const headingStyle = isTitle
    ? ({ fontSize: 13, fontFamily: 'Helvetica-Bold', color: INK, lineHeight: 1.4, marginBottom: 4 } as const)
    : ({
        fontSize: 11,
        fontFamily: 'Helvetica-Bold',
        color: INK,
        lineHeight: 1.4,
        marginBottom: 4,
      } as const);

  [...doc.body.childNodes].forEach(node => {
    const key = `h-${idx++}`;
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim();
      if (text) {
        blocks.push(
          <Text key={key} style={bodyStyle}>
            {text}
          </Text>,
        );
      }
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }
    const el = node as Element;
    const tag = el.tagName.toLowerCase();

    if (tag === 'p' || tag === 'div') {
      blocks.push(
        <Text key={key} style={bodyStyle}>
          {renderInlineChildren(el, key)}
        </Text>,
      );
    } else if (/^h[1-6]$/.test(tag)) {
      blocks.push(
        <Text key={key} style={headingStyle}>
          {renderInlineChildren(el, key)}
        </Text>,
      );
    } else if (tag === 'ul' || tag === 'ol') {
      let li = 0;
      [...el.children].forEach(child => {
        if (child.tagName.toLowerCase() !== 'li') {
          return;
        }
        li++;
        const liKey = `${key}-li-${li}`;
        blocks.push(
          <View key={liKey} style={{ flexDirection: 'row', marginBottom: 2, paddingLeft: 8 }}>
            <Text style={{ width: 14, fontSize: 10, color: MUTED }}>{tag === 'ol' ? `${li}.` : '•'}</Text>
            <Text style={{ flex: 1, fontSize: 10, color: MUTED, lineHeight: 1.5 }}>
              {renderInlineChildren(child, liKey)}
            </Text>
          </View>,
        );
      });
    } else if (tag === 'br') {
      blocks.push(
        <Text key={key} style={bodyStyle}>
          {'\n'}
        </Text>,
      );
    } else {
      blocks.push(
        <Text key={key} style={bodyStyle}>
          {renderInlineChildren(el, key)}
        </Text>,
      );
    }
  });

  return <>{blocks}</>;
};

const stripHtml = (html: string | null | undefined): string => {
  if (!html) {
    return '';
  }
  return html
    .replace(/<\/?[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ')
    .trim();
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) {
    return '—';
  }
  return new Date(value).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const QUESTION_TYPE_LABEL: Record<string, string> = {
  short_answer: 'Answer',
  multiple_choice: 'Multiple Choice',
  multiple_select: 'Multiple Select',
  true_false: 'True / False',
  ordering: 'Ordering',
  file_upload: 'File Upload',
  accept: 'Accept',
  accept_with_signature: 'Accept + Signature',
  dropdown: 'Dropdown',
};

const fileNameFromUrl = (url: string): string => {
  try {
    const path = new URL(url).pathname;
    const last = path.split('/').filter(Boolean).pop() || url;
    return decodeURIComponent(last.replace(/^\d+-/, ''));
  } catch {
    return url.split('/').pop() || url;
  }
};

interface QuestionPdfBlockProps {
  index: number;
  question: TFormQuestion;
  answer: TSubmissionResult['answers'][number] | undefined;
}

const QuestionPdfBlock = ({ index, question, answer }: QuestionPdfBlockProps) => {
  const typeLabel = QUESTION_TYPE_LABEL[question.questionType] ?? question.questionType;
  const sortedOptions = [...question.options].sort((a, b) => a.position - b.position);
  const selectedIds = new Set(answer?.selectedOptionIds ?? []);
  const orderingIds = answer?.orderingSequence ?? [];
  const textAnswer = answer?.textAnswer?.trim() ?? '';
  const fileUrls = answer?.fileUrls ?? [];

  const hasAnswer = (() => {
    switch (question.questionType) {
      case 'short_answer':
      case 'dropdown': {
        return textAnswer.length > 0;
      }
      case 'ordering': {
        return orderingIds.length > 0;
      }
      case 'file_upload': {
        return fileUrls.length > 0;
      }
      case 'accept': {
        return textAnswer === 'accepted';
      }
      case 'accept_with_signature': {
        try {
          const parsed = JSON.parse(textAnswer || '{}') as { checked?: boolean; name?: string };
          return parsed.checked === true && (parsed.name?.trim().split(/\s+/).filter(Boolean).length ?? 0) >= 2;
        } catch {
          return false;
        }
      }
      default: {
        return selectedIds.size > 0;
      }
    }
  })();

  const questionTextIsHtml = /<[^>]+>/.test(question.questionText);

  return (
    <View style={styles.card}>
      <View style={styles.cardHeader}>
        <Text style={styles.qBadge}>Q{index + 1}</Text>
        <Text style={styles.qType}>{typeLabel}</Text>
        {!hasAnswer && <Text style={styles.qUnanswered}>NOT ANSWERED</Text>}
      </View>

      {questionTextIsHtml ? (
        <View style={{ marginBottom: 4 }}>{renderHtmlToPdf(question.questionText, 'title')}</View>
      ) : (
        <Text style={styles.qText}>{question.questionText}</Text>
      )}
      {question.questionBody && <View style={{ marginBottom: 8 }}>{renderHtmlToPdf(question.questionBody)}</View>}

      {/* File upload — list uploaded files as clickable links */}
      {question.questionType === 'file_upload' && (
        <>
          <Text style={styles.sectionLabel}>Uploaded Files</Text>
          {hasAnswer ? (
            <View>
              {fileUrls.map(url => {
                const ext = (url.split('?')[0].split('.').pop() || 'FILE').toUpperCase().slice(0, 4);
                return (
                  <View key={url} style={styles.fileItem}>
                    <Text style={styles.fileIconBox}>{ext}</Text>
                    <Link src={url} style={styles.fileLink}>
                      {fileNameFromUrl(url)}
                    </Link>
                  </View>
                );
              })}
            </View>
          ) : (
            <Text style={styles.answerEmpty}>No files uploaded</Text>
          )}
        </>
      )}

      {/* Options panel — always show all available choices, selected ones highlighted */}
      {question.questionType !== 'short_answer' &&
        question.questionType !== 'file_upload' &&
        question.questionType !== 'ordering' &&
        question.questionType !== 'accept' &&
        question.questionType !== 'accept_with_signature' &&
        question.questionType !== 'dropdown' &&
        sortedOptions.length > 0 && (
          <>
            <Text style={styles.sectionLabel}>Options</Text>
            <View style={styles.pillRow}>
              {sortedOptions.map(opt => (
                <Text key={opt.id} style={selectedIds.has(opt.id) ? styles.pillSelected : styles.pillOff}>
                  {selectedIds.has(opt.id) ? '✓ ' : ''}
                  {opt.optionText}
                </Text>
              ))}
            </View>
            {!hasAnswer && (
              <>
                <Text style={styles.sectionLabel}>Your Answer</Text>
                <Text style={styles.answerEmpty}>No answer provided</Text>
              </>
            )}
          </>
        )}

      {/* Short answer — text the user typed */}
      {question.questionType === 'short_answer' && (
        <>
          <Text style={styles.sectionLabel}>Your Answer</Text>
          {hasAnswer ? (
            <View style={styles.textBlock}>
              <Text style={styles.answer}>{textAnswer}</Text>
            </View>
          ) : (
            <Text style={styles.answerEmpty}>No answer provided</Text>
          )}
        </>
      )}

      {/* Ordering — show user's sequence; falls back to listing the options */}
      {question.questionType === 'ordering' && (
        <>
          <Text style={styles.sectionLabel}>{hasAnswer ? 'Your Order' : 'Options'}</Text>
          {hasAnswer ? (
            <View>
              {orderingIds
                .map(id => sortedOptions.find(o => o.id === id))
                .filter((o): o is (typeof sortedOptions)[number] => !!o)
                .map((opt, i) => (
                  <View key={opt.id} style={styles.orderingItem}>
                    <Text style={styles.orderingNumber}>{i + 1}.</Text>
                    <Text style={styles.answer}>{opt.optionText}</Text>
                  </View>
                ))}
            </View>
          ) : (
            <>
              <View>
                {sortedOptions.map(opt => (
                  <View key={opt.id} style={styles.bulletItem}>
                    <Text style={styles.bulletDot}>• </Text>
                    <Text style={styles.answer}>{opt.optionText}</Text>
                  </View>
                ))}
              </View>
              <Text style={[styles.answerEmpty, { marginTop: 4 }]}>No order submitted</Text>
            </>
          )}
        </>
      )}

      {/* Accept — show accepted state as a pill */}
      {question.questionType === 'accept' && (
        <>
          <Text style={styles.sectionLabel}>Your Answer</Text>
          {hasAnswer ? (
            <View style={styles.pillRow}>
              <Text style={styles.pillSelected}>✓ Accepted</Text>
            </View>
          ) : (
            <Text style={styles.answerEmpty}>No answer provided</Text>
          )}
        </>
      )}

      {/* Accept with signature — show checkbox + name */}
      {question.questionType === 'accept_with_signature' && (
        <>
          <Text style={styles.sectionLabel}>Signature</Text>
          {hasAnswer ? (
            (() => {
              try {
                const parsed = JSON.parse(textAnswer) as { checked?: boolean; name?: string };
                return (
                  <View style={styles.textBlock}>
                    <Text style={styles.answer}>✓ Acknowledged · Signed: {parsed.name?.trim() ?? ''}</Text>
                  </View>
                );
              } catch {
                return <Text style={styles.answerEmpty}>No answer provided</Text>;
              }
            })()
          ) : (
            <Text style={styles.answerEmpty}>No answer provided</Text>
          )}
        </>
      )}

      {/* Dropdown — show selected value */}
      {question.questionType === 'dropdown' && (
        <>
          <Text style={styles.sectionLabel}>Your Selection</Text>
          {hasAnswer ? (
            <View style={styles.textBlock}>
              <Text style={styles.answer}>{textAnswer}</Text>
            </View>
          ) : (
            <Text style={styles.answerEmpty}>No answer provided</Text>
          )}
        </>
      )}
    </View>
  );
};

interface SubmissionPdfProps {
  submission: TSubmissionResult;
  formTitle: string;
  questions: TFormQuestion[];
  spaceName?: string;
  logoSrc?: string;
}

export const SubmissionPdf = ({ submission, formTitle, questions, spaceName, logoSrc }: SubmissionPdfProps) => {
  const cleanSpaceName = stripHtml(spaceName);
  const sortedQuestions = [...questions].sort((a, b) => a.position - b.position);
  const answersByQuestion = new Map(submission.answers.map(a => [a.questionId, a]));
  const answeredCount = sortedQuestions.filter(q => {
    const ans = answersByQuestion.get(q.id);
    if (!ans) {
      return false;
    }
    if (q.questionType === 'short_answer' || q.questionType === 'dropdown') {
      return !!ans.textAnswer?.trim();
    }
    if (q.questionType === 'ordering') {
      return ans.orderingSequence.length > 0;
    }
    if (q.questionType === 'file_upload') {
      return ans.fileUrls.length > 0;
    }
    if (q.questionType === 'accept') {
      return ans.textAnswer === 'accepted';
    }
    if (q.questionType === 'accept_with_signature') {
      try {
        const parsed = JSON.parse(ans.textAnswer ?? '{}') as { checked?: boolean; name?: string };
        return parsed.checked === true && (parsed.name?.trim().split(/\s+/).filter(Boolean).length ?? 0) >= 2;
      } catch {
        return false;
      }
    }
    return ans.selectedOptionIds.length > 0;
  }).length;

  return (
    <Document title={`${formTitle} - Submission #${submission.submissionNumber}`} author="Restore Your Core">
      <Page size="A4" style={styles.page} wrap>
        <View style={styles.header} fixed>
          {logoSrc ? <Image src={logoSrc} style={styles.logo} /> : <View style={styles.logo} />}
          <View style={styles.headerRight}>
            <Text style={styles.headerEyebrow}>
              {cleanSpaceName ? (
                <>
                  <Text style={styles.headerEyebrowSpace}>{cleanSpaceName}</Text>
                  {'  |  FORM SUBMISSION'}
                </>
              ) : (
                'FORM SUBMISSION'
              )}
            </Text>
            <Text style={styles.headerTitle}>{formTitle}</Text>
          </View>
        </View>
        <View style={styles.accentStrip} fixed />

        <View style={styles.metaWrap}>
          <View style={styles.metaRow}>
            <View style={styles.metaItem}>
              <Text style={styles.metaLabel}>Submission</Text>
              <Text style={styles.metaValue}>#{submission.submissionNumber}</Text>
            </View>
            <View style={styles.metaItem}>
              <Text style={styles.metaLabel}>Submitted</Text>
              <Text style={styles.metaValue}>{formatDate(submission.submittedAt)}</Text>
            </View>
            <View style={styles.metaItem}>
              <Text style={styles.metaLabel}>Started</Text>
              <Text style={styles.metaValue}>{formatDate(submission.startedAt)}</Text>
            </View>
            <View style={styles.metaItem}>
              <Text style={styles.metaLabel}>Answered</Text>
              <Text style={styles.metaValue}>
                {answeredCount} / {sortedQuestions.length}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.sectionDivider} />

        <View style={styles.body}>
          {sortedQuestions.map((question, idx) => (
            <QuestionPdfBlock
              key={question.id}
              index={idx}
              question={question}
              answer={answersByQuestion.get(question.id)}
            />
          ))}
        </View>

        <View style={styles.footer} fixed>
          <Text style={styles.footerBrand}>RESTORE YOUR CORE</Text>
          <Text
            style={styles.footerPage}
            render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
          />
        </View>
      </Page>
    </Document>
  );
};

const sanitizeFilename = (name: string): string =>
  name
    .replace(/[/\\?%*:|"<>]/g, '')
    .replace(/\s+/g, '-')
    .slice(0, 80);

/**
 * Render the site SVG logo to a PNG data URL so @react-pdf/renderer can embed
 * it via <Image>. We recolor `currentColor` to the brand pink (matches the
 * site header's tinted logo) before rasterizing.
 */
const loadLogoAsPng = async (): Promise<string | undefined> => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    const res = await fetch('/site-logo.svg');
    if (!res.ok) {
      return undefined;
    }
    const raw = await res.text();
    const recolored = raw.replace(/currentColor/g, BRAND);
    const blob = new Blob([recolored], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new window.Image();
        el.onload = () => resolve(el);
        el.onerror = err => reject(err);
        el.src = url;
      });
      // Render at ~3x for sharp PDF output.
      const targetWidth = 330;
      const ratio = img.naturalWidth ? img.naturalHeight / img.naturalWidth : 0.44;
      const targetHeight = Math.round(targetWidth * ratio);
      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return undefined;
      }
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
      return canvas.toDataURL('image/png');
    } finally {
      URL.revokeObjectURL(url);
    }
  } catch (error) {
    console.warn('Failed to load site logo for PDF', error);
    return undefined;
  }
};

export const downloadSubmissionPdf = async (
  submission: TSubmissionResult,
  formTitle: string,
  questions: TFormQuestion[],
  spaceName?: string,
): Promise<void> => {
  const logoSrc = await loadLogoAsPng();
  const blob = await pdf(
    <SubmissionPdf
      submission={submission}
      formTitle={formTitle}
      questions={questions}
      spaceName={spaceName}
      logoSrc={logoSrc}
    />,
  ).toBlob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${sanitizeFilename(formTitle)}-submission-${submission.submissionNumber}.pdf`;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
